import { useState, useRef } from 'react';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import { Card, CardBody, Button } from '@vnetwork/ui/primitives';
import { useToast } from '@vnetwork/ui/feedback';
import { useCreateDomain, useCreateDomainForService, type CDNDomain } from '@/api/manticore';
import { trackDomainAdd } from '@/lib/analytics-events';
import { serviceApiV3 } from '@/api/client';
import { errorMessage } from '@/api/error-message';
import { STEPS, DEFAULT_DRAFT, type DomainDraft } from './wizard-types';
import { WAF_FORM_DEFAULT } from '../security/waf/waf-types';
import { StepOrigin } from './StepOrigin';
import { StepDns } from './StepDns';
import { StepSsl } from './StepSsl';
import { StepWaf } from './StepWaf';
import styles from './DomainSetupWizard.module.css';

/**
 * Guided 4-step domain onboarding (Origin → DNS → SSL → WAF) — the single entry
 * point for adding a domain. Creation posts domain + origin + the chosen SSL
 * policy; DNS/SSL/WAF steps are skippable so a domain can be created and
 * configured later from its detail tabs.
 *
 * WAF enablement happens as a best-effort PATCH after domain creation. A failure
 * there shows a warning toast but never rolls back the domain — the user can
 * enable WAF from the domain's Security tab at any time.
 */
export function DomainSetupWizard() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { serviceId: scopedServiceId } = useParams<{ serviceId?: string }>();
  // The launching console scopes the new domain to its CDN service so the domain
  // binds to that family's core provider. When mounted inside the scoped console
  // (/services/cdn/:serviceId/domains/new) the id comes from the path; the legacy
  // top-level entry passes it as ?service=…. Without either the BE falls back to
  // the org's default CDN subscription.
  const serviceId = scopedServiceId ?? params.get('service') ?? '';
  const { show } = useToast();
  const [stepIndex, setStepIndex] = useState(0);
  const [draft, setDraft] = useState<DomainDraft>(DEFAULT_DRAFT);
  const set = (patch: Partial<DomainDraft>) => setDraft((d) => ({ ...d, ...patch }));
  // Tracks "user clicked Skip on the WAF step" — a ref avoids the stale-closure
  // problem: React batches the setDraft + mutate calls so onSuccess would see the
  // old draft.wafEnabled without this ref.
  const skipWafRef = useRef(false);

  const step = STEPS[stepIndex];
  const isLast = stepIndex === STEPS.length - 1;
  const canContinue = step.id === 'origin' ? draft.domain.trim().length > 0 : true;

  const mutationOptions = {
    onSuccess: async (d: CDNDomain | undefined) => {
      // Same event as the onboarding wizard's domain step — both paths create a
      // CDN domain, so they must emit one event (not a split feature_action here
      // vs domain_add there) or the domain-add count is undercounted.
      trackDomainAdd('cdn', d?.domain?.trim() || draft.domain.trim());
      const createdDomain = d?.domain?.trim() || d?.id;

      // P2: when the wizard runs inside a scoped CDN service console, navigate back
      // to that console rather than the org-wide domain detail. This keeps the user
      // in the service context they came from. Fallback: domain detail → CDN root.
      const destination = serviceId
        ? `/services/cdn/${serviceId}`
        : createdDomain
          ? `/services/domains/${createdDomain}`
          : '/services/cdn';

      // WAF enable — post-create best-effort PATCH. Called only when the user left
      // WAF enabled in the draft AND did not click Skip (skipWafRef guards against
      // the React batching stale-closure where draft.wafEnabled would read old state).
      // Failure is non-blocking: the domain is already created and live; the user
      // can enable WAF from Security → WAF tab at any time.
      // Enabling the WAF firewall = writing the domain's WAF config (managed
      // rules on, Monitor mode) via PUT /cdn/domains/:domainId/waf — the same
      // contract WafTab uses. NOT /waf/domains/waf-enable, which toggles Origin
      // Shield (a different vendor feature), not the WAF engine.
      const createdDomainId = d?.id;
      if (!skipWafRef.current && draft.wafEnabled && createdDomainId) {
        try {
          await serviceApiV3.put(`/cdn/domains/${createdDomainId}/waf`, {
            ...WAF_FORM_DEFAULT,
            enabled: true,
            mode: 'detect',
          });
          show({ tone: 'success', title: `WAF is protecting ${createdDomain}` });
        } catch {
          show({
            tone: 'warning',
            title: 'Domain created — WAF enable failed',
            description: 'Your domain is live. Enable WAF from Security → WAF tab.',
          });
        }
      } else {
        show({ tone: 'success', title: 'Domain created' });
      }

      navigate(destination);
    },
    onError: (e: unknown) =>
      show({
        tone: 'danger',
        title: 'Could not create domain',
        description: errorMessage(e, 'Could not create domain'),
      }),
  };
  const createScoped = useCreateDomainForService(serviceId, mutationOptions);
  const createLegacy = useCreateDomain(mutationOptions);
  const create = serviceId ? createScoped : createLegacy;

  const submit = () => {
    skipWafRef.current = false; // normal submit — honour draft.wafEnabled
    const domain = draft.domain.trim();
    const origin = draft.origin.trim();
    // SSL mode → upstream TLS flags (the edge↔origin leg lives in `upstream`,
    // not `tls`): flexible = plain HTTP to origin; strict additionally verifies
    // the origin certificate. Same wire shape as the upstream tab (form.ts).
    // Send only what the user chose — no protocol/selector defaults. Each
    // vendor handler fills its own required defaults; hardcoded vedge-flavored
    // values here would be pushed verbatim to every provider in the family.
    create.mutate({
      domain,
      upstream: origin
        ? {
            urls: [`${draft.originScheme}://${origin}`],
            host_header: domain,
            tls_to_origin: draft.sslMode !== 'flexible',
            origin_tls_verify: draft.sslMode === 'strict',
          }
        : undefined,
      tls: { min_version: draft.minTls },
    });
  };

  const advance = () => (isLast ? submit() : setStepIndex((i) => i + 1));

  // Skipping the last (WAF) step: create the domain without WAF enablement.
  // Set the ref BEFORE calling submit so the onSuccess closure sees it — the
  // ref is mutable and not subject to React's batched state flush.
  const skipAndSubmit = () => {
    skipWafRef.current = true;
    submit();
  };

  return (
    <div className={styles.page}>
      <div className={styles.eyebrow}>Set up a domain</div>

      <Card className={styles.card}>
        <CardBody className={styles.cardInner}>
          <div className={styles.stepHead}>
            <h2 className={styles.stepTitle}>{step.title}</h2>
            <p className={styles.stepDesc}>{step.description}</p>
          </div>

          {/* keyed remount replays the slide-up animation on each step change */}
          <div key={step.id} className={styles.stepBody}>
            {step.id === 'origin' && <StepOrigin draft={draft} set={set} />}
            {step.id === 'dns' && <StepDns draft={draft} />}
            {step.id === 'ssl' && <StepSsl draft={draft} set={set} />}
            {step.id === 'waf' && <StepWaf draft={draft} set={set} />}
          </div>

          <div className={styles.footer}>
            {stepIndex > 0 && (
              <Button variant="ghost" onClick={() => setStepIndex((i) => i - 1)}>
                Back
              </Button>
            )}
            <div className={styles.footerEnd}>
              {/* Non-last skippable steps advance without creating. The last (WAF)
                  step skip creates the domain immediately with WAF disabled. */}
              {step.skippable && !isLast && (
                <Button variant="ghost" onClick={() => setStepIndex((i) => i + 1)}>
                  Skip
                </Button>
              )}
              {step.skippable && isLast && (
                <Button variant="ghost" disabled={create.isPending} onClick={skipAndSubmit}>
                  Skip
                </Button>
              )}
              <Button disabled={!canContinue || create.isPending} onClick={advance}>
                {isLast
                  ? create.isPending
                    ? 'Creating…'
                    : 'Create'
                  : 'Continue'}
              </Button>
            </div>
          </div>
        </CardBody>
      </Card>

      <ol className={styles.rail}>
        {STEPS.map((s, i) => {
          const state = i === stepIndex ? 'active' : i < stepIndex ? 'done' : 'pending';
          return (
            <li
              key={s.id}
              className={
                state === 'active'
                  ? `${styles.railItem} ${styles.railItemActive}`
                  : state === 'done'
                    ? `${styles.railItem} ${styles.railItemDone}`
                    : styles.railItem
              }
            >
              <span
                className={
                  state === 'active'
                    ? `${styles.bullet} ${styles.bulletActive}`
                    : state === 'done'
                      ? `${styles.bullet} ${styles.bulletDone}`
                      : styles.bullet
                }
              />
              {s.label}
            </li>
          );
        })}
      </ol>
    </div>
  );
}
